import { useEffect, useState } from "react";

import { AutoComplete, Select, Button, Alert, Form, Spin } from "antd";
import { debounce } from "lodash";

import notifier from "utils/notifier";
import { ErrorResponse } from "utils/types";

import { CaseManagementModel } from "store/eligibility/actions";
import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import FloatLabel from "components/Common/FloatLabel";

interface SearchTaskFormProps {
  getCMWorkList(searchModel: CaseManagementModel): any;
  getMember(val: any, userId: string): any;
  getTaskOwners(val: any, userId: string): any;
  newTableDataHandler: any;
  isFetchingCM: boolean;
  isFetchingMember: boolean;
  isFetchingOwner: boolean;
}

const { Option } = Select;

const SearchTaskForm = (props: SearchTaskFormProps) => {

  const [memberOptions, setMemberOptions] = useState<any>([]);
  const [taskOwnerOptions, setTaskOwnerOptions] = useState<any>([]);
  const [showTable, setShowTable] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [membMpiNo, setMembMpiNo] = useState("");
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    getCMWorkList();
  }, [companyId, membMpiNo]);


  const getCMWorkList = () => {
    //On first page load, all values are null on the first API call. I cannot set null for the inputs on the UI side, so doing conditions below
    const cmWorkListParams = {
      CompanyId: companyId === "" ? null : companyId,
      DueDate: dueDate === "" ? null : dueDate,
      MembMpiNo: membMpiNo === "" ? null : membMpiNo,
      PageIndex: 0,
      PageSize: 100,
      ReviewDate: nextReviewDate === "" ? null : nextReviewDate,
      TaskOwner: form.getFieldValue('taskOwnerText') === "" ? null : form.getFieldValue('taskOwnerText'),
      UserId: sessionStorage.user_id,
    };

    return props.getCMWorkList(cmWorkListParams)
      .then((response: Object[]) => {
        setShowTable(response.length > 0 ? true : false);
        props.newTableDataHandler(response);
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response?.data?.Message || "Something went wrong, please contact a system administrator.")
      );
  };

  const getMember = (searchText: string) => {
    if (searchText.length < 3) return;
    return props.getMember(searchText, sessionStorage.user_id)
      .then((response: any) => {
        setMemberOptions(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getTaskOwners = (searchText: string) => {
    if (searchText.length < 3) return;
    return props.getTaskOwners(searchText, sessionStorage.user_id)
      .then((response: any) => {
        setTaskOwnerOptions(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const onChangeMember = (input: string) => {
    if (input.length === 0) {
      form.setFieldsValue({ searchMemberText: "" });
      setCompanyId("");
      setMembMpiNo("");
    }
  }

  const onSelectMember = (searchResult: string) => {
    const result = JSON.parse(searchResult);
    form.setFieldsValue({ searchMemberText: result.Result });
    setCompanyId(result.RmiCompanyId);
    setMembMpiNo(result.RmiMembMpiNo);
  };

  const onChangeTaskOwner = (input: string) => {
    if (input.length === 0) {
      form.setFieldsValue({ taskOwnerText: "" });
      getCMWorkList();
    }
  }

  const onSelectTaskOwner = (searchResult: string) => {
    const result = JSON.parse(searchResult);
    form.setFieldsValue({ taskOwnerText: result });
    getCMWorkList();
  };

  const resetFields = () => {
    setCompanyId('');
    setDueDate('');
    setMembMpiNo('');
    setNextReviewDate('');
    form.resetFields(['searchMemberText', 'taskOwnerText']);
  }

  return (
    <>
      <Form
        form={form}
        name="member-search-form"
        onFinish={getCMWorkList}
      >
        <div className="member-search row flex-lg-wrap justify-content-start mb-3 ml-0">
          <div className="col-lg-8 col-md-8">
            <FloatLabel
              label="Enter Member ID, Name, or DOB:"
              name="searchMemberText"
              form={form}
            >
              <AutoComplete
                style={{ width: '100%' }}
                onChange={(data) => onChangeMember(data)}
                onSelect={(data) => onSelectMember(data)}
                onSearch={debounce(getMember, 500)}
              >
                 {props.isFetchingMember ? <Option value=""><Spin /></Option> : memberOptions.map((value: any, key: number) => (
                  <Option key={key} value={JSON.stringify(value)}>
                    {value.Result}
                  </Option>
                ))}
              </AutoComplete>
            </FloatLabel>
          </div>

          <div className="col-lg-4 col-md-4">
            <FloatLabel
              label="Task Owner:"
              name="taskOwnerText"
              form={form}
            >
              <AutoComplete
                style={{ width: '100%' }}
                onChange={(data) => onChangeTaskOwner(data)}
                onSelect={(data) => onSelectTaskOwner(data)}
                onSearch={debounce(getTaskOwners, 500)}
              >
                {props.isFetchingOwner ? <Option value=""><Spin /></Option> : taskOwnerOptions.map((value: any, key: number) => (
                  <Option key={key} value={JSON.stringify(value)}>
                    {value}
                  </Option>
                ))}
              </AutoComplete>
            </FloatLabel>
          </div>

          <div className="col-lg-4 col-md-4">
            <FloatLabel
              name="dueDate"
              label="Due Date"
              value={dueDate}
            >
              <DatePickerLocalised
                date={dueDate}
                setDate={setDueDate}
                formInstance={form}
                formItemName={'dueDate'}
              />
            </FloatLabel>
          </div>

          <div className="col-lg-4 col-md-4">
            <FloatLabel
              name="nextReviewDate"
              label="Next Review Date"
              value={nextReviewDate}
            >
              <DatePickerLocalised
                date={nextReviewDate}
                setDate={setNextReviewDate}
                formInstance={form}
                formItemName={'nextReviewDate'}
              />
            </FloatLabel>
          </div>

          <div className="col-lg-2 col-md-2">
            <div className="btn-wrapper">
              <Button
                className="member-search-btn"
                onClick={getCMWorkList}
                loading={props.isFetchingCM}
              >
                SEARCH
              </Button>
            </div>
          </div>
          <div className="col-lg-2 col-md-2">
            <div className="btn-wrapper">
              <Button className="reset-btn" onClick={resetFields}>
                RESET
              </Button>
            </div>
          </div>
        </div>
      </Form>

      {!showTable && (
        <Alert
          style={{ textAlign: 'center' }}
          message={<>There were no results for this search. Please check the fields and try again.</>}
          type="error"
          className="col-lg-24"
        />
      )}
    </>
  );
}

export default SearchTaskForm;